import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { OFF_ACTION_CLASS } from 'components/buttons/buttonStyle';
import DrawerPortal from 'components/drawer/DrawerPortal';
import EmptyState from 'components/EmptyState';
import { Check as CheckIcon, NotificationsNoneOutlined, XIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import useAuth from 'hooks/useAuth';
import { useIsMobile } from 'hooks/useIsMobile';
import { useDispatch, useSelector } from 'store';
import {
    APINotificationsSlice,
    useArchiveNotificationMutation,
    useGetNotificationsListQuery,
    useReadNotificationMutation,
    useReadNotificationsMutation,
} from 'store/api/notifications';
import { useGetTasksAddressedToEmployeeQuery } from 'store/api/tasks';
import { openDrawer } from 'store/reducers/menu';
import { closeTemporaryDrawer, toggleTemporaryDrawer } from 'store/reducers/temporaryDrawer';
import { FeatureFlag } from 'types/features';
import { AutomatedNotification } from 'types/notifications';
import { AddressedTaskListItem, TaskStatusType, TaskTypeCode } from 'types/tasks';
import { dismissAlert, successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

import { CategoryFilter, CategoryOption, InboxCategoryFilter } from './InboxCategoryFilter';
import { FeedEntry, groupByDay, INBOX_CATEGORIES, InboxCategory } from './inboxFeed';
import { ComplianceTaskFeedItem, InboxFeedItem } from './InboxFeedItem';

export const INBOX_DRAWER_ID = 'inbox';
const INBOX_TITLE_ID = `${INBOX_DRAWER_ID}-title`;
/** The empty feed's title, as its empty state shows it and as the panel's live region says it. */
const EMPTY_TITLE_ID = 'notifications.empty.title';
/** The title of a feed that did not load, shown and said the same way. */
const ERROR_TITLE_ID = 'notifications.feed.error.title';

/** An open task of the compliance type, stored or automatic: the Inbox's Compliance tasks. */
const isOpenComplianceTask = (task: AddressedTaskListItem): boolean =>
    task.taskStatusTypeCode !== TaskStatusType.DONE &&
    !!task.taskTypeCodes?.some((code) => code === TaskTypeCode.COMPLIANCE_TASK);

/**
 * What the Inbox lists: the classic header's Notifications and Tasks queries, with the same polling,
 * so the two designs share one cache entry each.
 */
function useInboxData() {
    const { user } = useAuth();
    const userId = user?.id;
    const skip = typeof userId !== 'number';
    const notifications = useGetNotificationsListQuery({}, { pollingInterval: 240000 });
    const tasks = useGetTasksAddressedToEmployeeQuery(
        { employeeId: userId ?? 0, search: '' },
        { skip, pollingInterval: 600000 }
    );
    const queries = [notifications, tasks];
    return {
        notifications: notifications.data ?? [],
        tasks: tasks.data ?? [],
        isLoading: queries.some((query) => query.isLoading),
        /** One of the two lists failed, so what the others hold is not the whole Inbox. */
        isError: queries.some((query) => query.isError),
        /**
         * A list never arrived: what it holds is unknown, not nothing, so neither the unread count nor a view that
         * came out empty can be trusted. A list whose poll failed keeps what it had.
         */
        isMissingList: queries.some((query) => query.isError && query.data === undefined),
        /** Asks again for the lists that failed. */
        refetch: () => {
            for (const query of queries) {
                if (query.isError) {
                    void query.refetch();
                }
            }
        },
    };
}

/**
 * The My work badges: Inbox counts unread notifications, Tasks the open tasks. Undefined until the lists have
 * loaded.
 */
export function useMyWorkCounts(): { inbox?: number; tasks?: number } {
    const { notifications, tasks, isLoading } = useInboxData();
    if (isLoading) {
        return {};
    }
    return {
        inbox: notifications.filter((item) => !item.readAt).length,
        tasks: tasks.filter((task) => task.taskStatusTypeCode !== TaskStatusType.DONE).length,
    };
}

/** Opens the Inbox drawer, or closes it when it is the one open. */
export function useToggleInbox(): () => void {
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const isOpen = useSelector((state) => state.temporaryDrawer.activeDrawerId === INBOX_DRAWER_ID);
    return () => {
        // Under 768px the sidebar is an overlay: it gives way to the panel it opened.
        if (!isOpen && isMobile) dispatch(openDrawer({ drawerOpen: false }));
        dispatch(isOpen ? closeTemporaryDrawer() : toggleTemporaryDrawer(INBOX_DRAWER_ID));
    };
}

type Segment = 'all' | 'unread';
/** The empty state's second line, which is also read out: a message and its values. */
type EmptyBody = { id: string; values: Record<string, string> };

const CATEGORY_BY_ID = new Map(INBOX_CATEGORIES.map((category) => [category.id, category]));
const COMPLIANCE_TASKS_CATEGORY = CATEGORY_BY_ID.get('compliance-tasks') as InboxCategory;

const isUnread = (entry: FeedEntry): boolean => !entry.item.readAt;

const entryKey = (entry: FeedEntry): string => String(entry.item.id);

/** An archive that waits for its toast: `send` runs once, and never after Undo took it back. */
type PendingArchive = { send: () => void; toastId: string | number };

const GROUP_HEADING_CLASS = 'text-label text-muted-foreground bg-card px-6 pt-4 pb-1.5 uppercase';

function InboxContent() {
    const posthog = usePostHog();
    const dispatch = useDispatch();
    const { notifications, tasks, isLoading, isError, isMissingList, refetch } = useInboxData();
    const isCorrespondenceEnabled = useIsFeatureEnabled(FeatureFlag.CORRESPONDENCE_MODULE);
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { classifiers } = useClassifiers({ codes: ['TASK_PRIORITY_TYPE'] });
    const [segment, setSegment] = useState<Segment>('all');
    const [filter, setFilter] = useState<CategoryFilter>('all');
    // The rows archived here, out of the feed at once. A key stays until the Inbox closes: nothing takes an archive
    // back, so it never hides a row that returned.
    const [archived, setArchived] = useState<ReadonlySet<string>>(new Set());
    const pendingArchives = useRef(new Map<string, PendingArchive>());
    const readRequests = useRef(new Map<InboxCategory['id'], Promise<unknown>>());

    const [readNotification] = useReadNotificationMutation();
    const [readNotifications] = useReadNotificationsMutation();
    const [archiveNotification] = useArchiveNotificationMutation();

    const closeDrawer = useCallback(() => dispatch(closeTemporaryDrawer()), [dispatch]);

    // An archive still waiting for its toast is sent when the Inbox closes or the page goes: the row is already
    // gone, and its Undo goes with the toast.
    useEffect(() => {
        const pending = pendingArchives.current;
        const flush = () =>
            [...pending.values()].forEach(({ send, toastId }) => {
                send();
                dismissAlert(toastId);
            });
        window.addEventListener('pagehide', flush);
        return () => {
            window.removeEventListener('pagehide', flush);
            flush();
        };
    }, []);

    const categories = INBOX_CATEGORIES.filter(
        (category) => isCorrespondenceEnabled || category.id !== 'correspondence'
    );
    const categoryByNotification = new Map<string, InboxCategory>(
        categories.flatMap((category) =>
            category.notificationCategory ? [[category.notificationCategory, category] as const] : []
        )
    );

    // A notification of a category the Inbox does not list (Correspondence with its module off) stays out.
    const entries = notifications
        .flatMap((item): FeedEntry[] => {
            const category = categoryByNotification.get(item.category);
            return category ? [{ category, item }] : [];
        })
        .filter((entry) => !archived.has(entryKey(entry)));
    const complianceTasks = tasks.filter(isOpenComplianceTask);

    const inScope = filter === 'all' ? entries : entries.filter((entry) => entry.category.id === filter);
    const unreadInScope = inScope.filter(isUnread);
    const groups = groupByDay(segment === 'unread' ? unreadInScope : inScope, new Date());
    // The tasks have no read state, so the Unread view leaves them out.
    const shownTasks = segment === 'all' && (filter === 'all' || filter === 'compliance-tasks') ? complianceTasks : [];
    const selected = filter === 'all' ? undefined : CATEGORY_BY_ID.get(filter);

    const filterOptions: CategoryOption[] = categories.map((category) => {
        if (category.id === 'compliance-tasks') {
            const open = complianceTasks.length;
            return { category, countLabel: open ? t('notifications.feed.open', { count: String(open) }) : '' };
        }
        const unread = entries.filter((entry) => entry.category.id === category.id && isUnread(entry)).length;
        return { category, countLabel: unread ? t('notifications.unread', { count: String(unread) }) : '' };
    });
    const totalUnread = entries.filter(isUnread).length;

    // A read change shows at once: the rows are patched in the cache their list reads before the request leaves, and
    // the patch is taken back if the request fails, whose toast the API layer raises. The request turns no other
    // button off.
    // A request leaves once the last one of its category has settled: the single-row endpoint flips the row's state
    // on the server, so a flip that overtook a request for the same rows would leave them as the user did not.
    // shortcut: a flip queued behind a request that failed still flips, the other way; send the wanted state
    // instead once the single-row endpoint takes one.
    const changeRead = (rows: FeedEntry[], readAt: string | null, send: () => Promise<unknown>): Promise<unknown> => {
        const ids = new Set(rows.map((row) => row.item.id));
        const patch = dispatch(
            APINotificationsSlice.util.updateQueryData('getNotificationsList', {}, (items: AutomatedNotification[]) =>
                items.forEach((item) => {
                    if (ids.has(item.id)) item.readAt = readAt;
                })
            )
        );
        // Every call's rows are of one category.
        const category = rows[0].category.id;
        const previous = readRequests.current.get(category);
        const request = previous ? previous.then(send, send) : send();
        readRequests.current.set(category, request);
        return request.catch((error: unknown) => {
            patch.undo();
            throw error;
        });
    };
    // The read and archive requests of the classic Notifications drawer, with their events.
    const toggleRead = (entry: FeedEntry) => {
        const isMarkingRead = isUnread(entry);
        void changeRead([entry], isMarkingRead ? new Date().toISOString() : null, () =>
            readNotification({ id: entry.item.id, skipButtonDisable: true }).unwrap()
        )
            .then(() => {
                if (isMarkingRead) {
                    posthog.capture('notifications_marked_read', { category: entry.item.category, scope: 'one' });
                }
            })
            .catch(console.error);
    };
    // There is no way to take an archive back from the server, so the row leaves at once and the request waits
    // for the toast: Undo before it closes puts the row back and nothing is sent.
    const archive = (entry: FeedEntry) => {
        const key = entryKey(entry);
        const pending = pendingArchives.current;
        const restore = () =>
            setArchived((current) => {
                const next = new Set(current);
                next.delete(key);
                return next;
            });
        const send = () => {
            if (!pending.delete(key)) {
                return;
            }
            void archiveNotification({ id: entry.item.id })
                .unwrap()
                .then(() => posthog.capture('notification_archived', { category: entry.item.category, scope: 'one' }))
                .catch((error: unknown) => {
                    console.error(error);
                    restore();
                });
        };
        setArchived((current) => new Set(current).add(key));
        const toastId = successAlert(t('notifications.archived'), {
            action: {
                label: t('pins.undo'),
                onClick: () => {
                    pending.delete(key);
                    restore();
                },
            },
            // Closing the toast by hand is not an undo: the row is gone either way.
            onAutoClose: send,
            onDismiss: send,
        });
        pending.set(key, { send, toastId });
    };
    // Marks read what the filter covers: each of its notification categories as a whole. A request that fails
    // takes back its own rows only.
    const markAllRead = () => {
        const readAt = new Date().toISOString();
        const types = new Set(
            unreadInScope.flatMap((entry) =>
                entry.category.notificationCategory ? [entry.category.notificationCategory] : []
            )
        );
        const requests: Promise<unknown>[] = [...types].map((type) =>
            changeRead(
                unreadInScope.filter((entry) => entry.category.notificationCategory === type),
                readAt,
                () => readNotifications({ type, skipButtonDisable: true }).unwrap()
            ).then(() => posthog.capture('notifications_marked_read', { category: type, scope: 'all' }))
        );
        void Promise.all(requests).catch(console.error);
    };

    const changeFilter = (next: CategoryFilter) => {
        setFilter(next);
        const category = next === 'all' ? undefined : CATEGORY_BY_ID.get(next)?.notificationCategory;
        if (category) {
            posthog.capture('notification_tab_switched', { category });
        }
    };

    const selectedLabel = selected ? t(selected.labelId) : '';
    const unreadEmptyBody: EmptyBody = selected
        ? { id: 'notifications.feed.empty-unread-category', values: { category: selectedLabel } }
        : { id: 'notifications.feed.empty-unread', values: {} };
    const allEmptyBody: EmptyBody = selected
        ? { id: 'notifications.empty.body', values: { category: selectedLabel } }
        : { id: 'notifications.feed.empty-all', values: {} };
    const emptyBody = segment === 'unread' ? unreadEmptyBody : allEmptyBody;
    const isEmpty = groups.length === 0 && shownTasks.length === 0;
    // What loaded beside a list that failed still shows. An empty view is the failure's, where "all caught up"
    // would be a guess, while a list never arrived, and so is an Inbox with nothing at all to show once the last
    // request of a list failed.
    // shortcut: a list that never arrived marks every empty view failed, a filter it does not feed included; tell
    // them apart per list if that view has to read as empty while another list is down.
    const hasFailed = isEmpty && (isMissingList || (isError && entries.length === 0 && complianceTasks.length === 0));
    const emptyOutcome = hasFailed ? t(ERROR_TITLE_ID) : `${t(EMPTY_TITLE_ID)} ${t(emptyBody.id, emptyBody.values)}`;

    return (
        <div className="flex h-full flex-col overflow-hidden">
            <div className="flex items-center justify-between ps-6 pe-4 pt-4 pb-1">
                <h2 id={INBOX_TITLE_ID} className="text-section">
                    {t('menu.inbox')}
                </h2>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('close')}
                    className="text-fg-secondary"
                    onClick={closeDrawer}
                >
                    <XIcon className="size-[18px]" />
                </Button>
            </div>
            <Tabs
                value={segment}
                onValueChange={(next: Segment) => setSegment(next)}
                className="flex min-h-0 flex-1 flex-col gap-0"
            >
                <div className="border-divider-soft flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b px-6 pt-2.5 pb-3.5 max-sm:px-4">
                    <TabsList>
                        <TabsTrigger value="all">{t('notifications.feed.all')}</TabsTrigger>
                        <TabsTrigger value="unread">
                            {isLoading || isMissingList
                                ? t('notifications.feed.unread-tab')
                                : t('notifications.feed.unread', { count: String(totalUnread) })}
                        </TabsTrigger>
                    </TabsList>
                    <InboxCategoryFilter
                        value={filter}
                        options={filterOptions}
                        allCountLabel={totalUnread ? t('notifications.unread', { count: String(totalUnread) }) : ''}
                        onChange={changeFilter}
                    />
                    <span className="flex-1" />
                    {filter === 'compliance-tasks' ? null : (
                        // Off once nothing in scope is unread, it keeps the focus it was pressed with, so it dims
                        // itself: the kit dims only a natively disabled button.
                        <Button
                            variant="link"
                            size="xs"
                            className={cn(
                                'text-link text-meta h-auto gap-1 p-0 font-normal',
                                OFF_ACTION_CLASS,
                                'aria-disabled:text-link/50 aria-disabled:hover:no-underline'
                            )}
                            onClick={markAllRead}
                            disabled={buttonsDisabled || unreadInScope.length === 0}
                            focusableWhenDisabled
                        >
                            <CheckIcon className="size-4" />
                            {t('notifications.mark-all-as-read')}
                        </Button>
                    )}
                </div>
                <TabsContent value={segment} className="min-h-0 flex-1 overflow-auto overscroll-contain">
                    {groups.map((group) => (
                        <section key={group.id}>
                            <h3 className={GROUP_HEADING_CLASS}>{t(`notifications.feed.day.${group.id}`)}</h3>
                            <ul>
                                {group.entries.map((entry) => (
                                    <InboxFeedItem
                                        key={entryKey(entry)}
                                        entry={entry}
                                        onToggleRead={toggleRead}
                                        onArchive={archive}
                                        areButtonsDisabled={buttonsDisabled}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                    {shownTasks.length > 0 ? (
                        <section>
                            <h3 className={GROUP_HEADING_CLASS}>{t('notifications.feed.open-compliance-tasks')}</h3>
                            <ul>
                                {shownTasks.map((task, index) => (
                                    // The automatic tasks are synthetic and come without an id.
                                    <ComplianceTaskFeedItem
                                        key={task.id ?? `automatic-${index}`}
                                        task={task}
                                        category={COMPLIANCE_TASKS_CATEGORY}
                                        classifiers={classifiers}
                                    />
                                ))}
                            </ul>
                        </section>
                    ) : null}
                    {isLoading ? (
                        <div aria-hidden className="flex flex-col gap-5 px-6 pt-5">
                            {['w-2/3', 'w-1/2', 'w-3/5'].map((width) => (
                                <div key={width} className="flex flex-col gap-2">
                                    <Skeleton className={cn('h-3.5', width)} />
                                    <Skeleton className="h-3 w-full" />
                                </div>
                            ))}
                        </div>
                    ) : hasFailed ? (
                        <EmptyState
                            variant="error"
                            size="panel"
                            errorTitleId={ERROR_TITLE_ID}
                            onRetry={refetch}
                            surface="inbox"
                            className="py-24"
                        />
                    ) : isEmpty ? (
                        <EmptyState
                            size="panel"
                            icon={NotificationsNoneOutlined}
                            titleId={EMPTY_TITLE_ID}
                            bodyId={emptyBody.id}
                            bodyValues={emptyBody.values}
                            surface={`inbox-${segment}-${filter}`}
                            className="py-24"
                        />
                    ) : null}
                </TabsContent>
            </Tabs>
            {/* Says the feed came out empty or could not be loaded, as it loads and as a segment or a filter
                empties it. Mounted before its text, which a live region needs to be read. */}
            <div role="status" className="sr-only">
                {isEmpty && !isLoading ? emptyOutcome : ''}
            </div>
        </div>
    );
}

/**
 * The new design's Inbox: one drawer on the window's end side over what the classic Notifications and Tasks drawers
 * show — every notification category and the open compliance tasks, each with
 * its read and archive actions. They form one feed, grouped by day, with the categories in a filter
 * rather than a tab strip. The Tasks page stays the place for tasks.
 */
function Inbox() {
    return (
        <DrawerPortal
            content={<InboxContent />}
            drawerId={INBOX_DRAWER_ID}
            labelledBy={INBOX_TITLE_ID}
            applyColor="secondary"
            type="temporary"
            width="100%"
        />
    );
}

export default Inbox;
